import type { ReactNode } from 'react'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'

/**
 * Paper "03 - App · Settings": underline tabs (no icons, sentence case), then a
 * gray well of raised cards. Each card stacks rows of a 220px label column and
 * a 460px control column, divided by hairlines.
 */
export function SettingsTabs<T extends string>({
  tabs,
  active,
  onChange,
  label,
}: {
  tabs: readonly { id: T; label: string }[]
  active: T
  onChange: (id: T) => void
  label: string
}) {
  return (
    <div role="tablist" aria-label={label} className="mt-3.5 flex gap-[22px] overflow-x-auto border-b border-border scrollbar-hide">
      {tabs.map((tab) => {
        const isActive = tab.id === active
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(tab.id)}
            className={cn(
              'flex h-11 shrink-0 items-center whitespace-nowrap text-[13px] font-medium leading-4 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
              isActive
                ? 'text-foreground shadow-[inset_0_-2px_0_var(--foreground)]'
                : 'text-foreground/60 hover:text-foreground',
            )}
          >
            {tab.label}
          </button>
        )
      })}
    </div>
  )
}

export function SettingsBody({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-4 pt-6">{children}</div>
}

export function SettingsWell({ children }: { children: ReactNode }) {
  return <div className="well flex w-full max-w-[784px] flex-col gap-2">{children}</div>
}

export function SettingsCard({ children, danger }: { children: ReactNode; danger?: boolean }) {
  return (
    <section
      className={cn(
        'flex flex-col rounded-xl bg-card px-4 sm:px-6 [&>*+*]:border-t [&>*+*]:border-border',
        danger
          ? 'shadow-[0_0_0_1px_#FECDCA,0_10px_15px_-3px_rgb(0_0_0/0.05),0_4px_6px_-4px_rgb(0_0_0/0.05)] dark:shadow-[0_0_0_1px_rgb(240_68_56/0.35)]'
          : 'surface-raised',
      )}
    >
      {children}
    </section>
  )
}

export function SettingsRow({
  title,
  description,
  children,
}: {
  title: string
  description?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-4 py-6 md:flex-row md:gap-10">
      <div className="flex shrink-0 flex-col gap-1 md:w-[220px]">
        <h2 className="text-sm font-semibold leading-[18px] text-foreground">{title}</h2>
        {description && <p className="text-xs leading-[18px] text-foreground/60">{description}</p>}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-4 md:max-w-[460px]">{children}</div>
    </div>
  )
}

export function SettingsFooter({ children }: { children: ReactNode }) {
  return (
    <div className="flex py-4 md:gap-10">
      <div className="hidden shrink-0 md:block md:w-[220px]" />
      <div className="flex flex-1 justify-end gap-2 md:max-w-[460px]">{children}</div>
    </div>
  )
}

export function SettingsField({ label, htmlFor, children }: { label: string; htmlFor?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-[7px]">
      <label htmlFor={htmlFor} className="text-xs font-medium leading-4 text-foreground">
        {label}
      </label>
      {children}
    </div>
  )
}

/** Surface box with a title, hint and an ink switch; optional body below a hairline when on. */
export function SettingsToggle({
  title,
  description,
  checked,
  onCheckedChange,
  disabled,
  children,
}: {
  title: string
  description?: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  disabled?: boolean
  children?: ReactNode
}) {
  return (
    <div className={cn('flex flex-col rounded-lg border border-border bg-muted/50', disabled && 'opacity-50')}>
      <label className={cn('flex items-center gap-3 px-3.5 py-3', disabled ? 'cursor-not-allowed' : 'cursor-pointer')}>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-[13px] font-medium leading-4 text-foreground">{title}</span>
          {description && <span className="text-xs leading-4 text-foreground/60">{description}</span>}
        </span>
        <Switch
          checked={checked}
          disabled={disabled}
          onCheckedChange={onCheckedChange}
          className="h-5 w-[34px] shrink-0 disabled:opacity-100 data-[state=unchecked]:bg-input [&>span]:size-4 [&>span]:data-[state=checked]:translate-x-[15px]"
        />
      </label>
      {checked && children && <div className="flex flex-col gap-2 border-t border-border px-3.5 py-3">{children}</div>}
    </div>
  )
}

export function SettingsDangerAction({
  title,
  description,
  action,
  children,
}: {
  title: string
  description: ReactNode
  action?: ReactNode
  children?: ReactNode
}) {
  return (
    <>
      <div className="flex items-center gap-3.5">
        <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
          <p className="text-sm font-medium leading-[18px] text-danger">{title}</p>
          <p className="text-xs leading-4 text-foreground/60">{description}</p>
        </div>
        {action}
      </div>
      {children}
    </>
  )
}

/** Outline button tuned for settings: 34px, ink text; `danger` swaps to the danger ring. */
export const settingsButtonClass = 'h-[34px] rounded-lg px-3 text-xs font-medium shadow-none'
/** Saves inside the product are ink, never brand orange (one orange per view, DESIGN.md). */
export const settingsSaveClass = `${settingsButtonClass} bg-foreground text-background hover:bg-foreground/90`
export const settingsDangerOutlineClass =
  'h-[34px] shrink-0 rounded-lg border-[#FECDCA] bg-background px-[11px] text-xs font-medium text-danger shadow-none hover:bg-danger-tint hover:text-danger dark:border-danger/40'

/** Select trigger sized to the 36px settings inputs. */
export const settingsSelectTriggerClass = 'w-full rounded-lg bg-background px-3 text-sm data-[size=default]:h-9'

/** Paper "Tracking code" card anatomy: title + meta + trailing action, body below. For list-shaped settings (team, sites, links). */
export function SettingsListCard({
  title,
  description,
  action,
  children,
}: {
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
  children?: ReactNode
}) {
  return (
    <section className="surface-raised flex flex-col overflow-clip">
      <div className="flex items-center gap-2.5 px-[18px] pb-3 pt-4">
        <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
          <h2 className="flex items-center gap-2 text-sm font-semibold leading-[18px] text-foreground">{title}</h2>
          {description && <p className="text-xs leading-4 text-foreground/60">{description}</p>}
        </div>
        {action}
      </div>
      {children ? <div className="flex flex-col gap-3 px-[18px] pb-[18px]">{children}</div> : <div className="pb-1" />}
    </section>
  )
}

/** Hairline-divided list inside a settings card; rows are 52px with a trailing slot. */
export function SettingsList({ children }: { children: ReactNode }) {
  return <ul className="flex flex-col rounded-lg border border-border [&>li+li]:border-t [&>li+li]:border-border">{children}</ul>
}

export function SettingsListRow({
  primary,
  secondary,
  leading,
  trailing,
}: {
  primary: ReactNode
  secondary?: ReactNode
  leading?: ReactNode
  trailing?: ReactNode
}) {
  return (
    <li className="flex min-h-[52px] items-center gap-3 py-2 pl-3 pr-2">
      {leading}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-[13px] font-medium leading-4 text-foreground">{primary}</span>
        {secondary && <span className="truncate text-xs leading-4 text-foreground/60">{secondary}</span>}
      </div>
      {trailing && <div className="flex shrink-0 items-center gap-1">{trailing}</div>}
    </li>
  )
}
